import { useEffect, useState, useCallback, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { TextField, Button, Grid, Autocomplete, CircularProgress } from '@mui/material';
import { BaseModal } from '../../molecules';
import { useMoveToResponseCode, INewResponseCode, useGetResourceList } from '../../../hooks';
import { useSetSnackBar } from '../../../store/Snackbar.state';

interface MoveResponseCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  responseCode?: INewResponseCode;
  refetch: () => void;
}

const schema = z.object({
  internalErrorCode: z.string().nonempty('İç Hata Kodu zorunludur'),
});

export const MoveResponseCodeModal = ({ isOpen, onClose, responseCode, refetch }: MoveResponseCodeModalProps) => {
  const { control, handleSubmit, reset, formState: { errors } } = useForm<{ internalErrorCode: string }>({
    resolver: zodResolver(schema),
    defaultValues: { internalErrorCode: '' },
  });

  const setSnackbar = useSetSnackBar();
  const { mutate: moveToResponseCode, isLoading: isMoving } = useMoveToResponseCode();
  const { mutate: getResourceList } = useGetResourceList();

  const [options, setOptions] = useState<any[]>([]);
  const [page, setPage] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const fetchTimeout = useRef<NodeJS.Timeout>();

  const fetchOptions = useCallback((currentPage: any, currentInput: any) => {
    if (currentInput.length > 0 && currentInput.length < 3) {
      return;
    }
    setIsLoading(true);
    const params: any = {
      page: currentPage,
      size: 50,
      orderBy: 'Key',
      orderByDesc: false,
    };
    if (currentInput.length == 3) {
      params.Key = currentInput;
    } else if (currentInput.length > 3) {
      params.value = currentInput;
    }

    getResourceList(params, {
      onSuccess: (data: any) => {
        const newCodes = data?.data?.result?.map((code: { key: string; value: string }) => ({
          label: `${code.key} (${code.value})`,
          value: code.key,
        })) || [];

        setOptions(prev => currentPage === 0 ? newCodes : [...prev, ...newCodes]);
        setHasNextPage(newCodes.length === 50);
        setIsLoading(false);
      },
      onError: () => setIsLoading(false)
    });
  }, [getResourceList]);

  useEffect(() => {
    if (isOpen) {
      fetchOptions(0, '');
    } else {
      setOptions([]);
      setPage(0);
      setInputValue('');
      setHasNextPage(true);
    }
  }, [isOpen, fetchOptions]);

  const handleInputChange = (event: any, newInputValue: any, reason: any) => {
    if (reason === 'input') {
      setInputValue(newInputValue);
      clearTimeout(fetchTimeout.current);
      fetchTimeout.current = setTimeout(() => {
        setPage(0);
        fetchOptions(0, newInputValue);
      }, 500);
    }
  };

  const handleScroll = (event: React.SyntheticEvent) => {
    const listboxNode = event.currentTarget;
    if (listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 1) {
      if (hasNextPage && !isLoading) {
        const newPage = page + 1;
        setPage(newPage);
        fetchOptions(newPage, inputValue);
      }
    }
  };

  useEffect(() => {
    reset({ internalErrorCode: '' });
  }, [responseCode, reset]);

  const onSubmit = (data: { internalErrorCode: string }) => {
    if (!responseCode) return;

    moveToResponseCode({ id: responseCode.id, internalErrorCode: data.internalErrorCode }, {
      onSuccess: (res) => {
        if (res.isSuccess) {
          setSnackbar({ severity: 'success', isOpen: true, description: res.message });
          refetch();
          onClose();
        } else {
          setSnackbar({ severity: 'error', isOpen: true, description: res.message });
        }
      },
      onError: () => {
        setSnackbar({ severity: 'error', isOpen: true, description: 'İşlem sırasında bir hata oluştu' });
      },
    });
  };

  return (
    <BaseModal title="Ana Listeye Taşı" isOpen={isOpen} onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Controller
              name="internalErrorCode"
              control={control}
              render={({ field: { onChange, value } }) => {
                const foundOption = options.find(option => option.value === value);
                return (
                  <Autocomplete
                    freeSolo
                    options={options}
                    getOptionLabel={(option) => {
                      if (typeof option === 'string') return option;
                      return option.label || '';
                    }}
                    value={foundOption || value || null}
                    onChange={(_, newValue) => {
                      if (typeof newValue === 'string') {
                        onChange(newValue);
                      } else if (newValue) {
                        onChange(newValue.value);
                      } else {
                        onChange('');
                      }
                    }}
                    onInputChange={handleInputChange}
                    loading={isLoading}
                    ListboxProps={{
                      onScroll: handleScroll,
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="İç Hata Kodu"
                        fullWidth
                        error={!!errors.internalErrorCode}
                        helperText={errors.internalErrorCode?.message}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                )
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Button type="submit" variant="contained" disabled={isMoving || isLoading}>
              Taşı
            </Button>
          </Grid>
        </Grid>
      </form>
    </BaseModal>
  );
};
